import React from 'react';

import { NextPage } from 'next';
import { useRouter } from 'next/router';

import { LISTING_ROWS_PER_PAGE } from '@kamernet/constants/Listings';
import { AnalyticsPageCategory } from '@kamernet/core/Analytics/types';
import { getListingAnalyticsPageName } from '@kamernet/core/Analytics/utilities/getListingAnalyticsPageName';
import {
  FindListingsResponse,
  ListingRequest,
  ListingSearchCriteriaResponse,
} from '@kamernet/core/ApiClient/Kamernet';
import { getServerDataCache } from '@kamernet/core/DataCache/initializeServerDataCache';
import { logger } from '@kamernet/core/Logger';
import { PageLayout } from '@kamernet/core/PageLayout';
import { ROUTES } from '@kamernet/core/Routing';
import {
  TYPEANDCITY_EN_NL_MAPPING,
  TYPEANDCITY_NL_EN_MAPPING,
} from '@kamernet/core/Routing/dynamicRouterMatcher';

import { definePageLoader } from '@kamernet/modules/definePageLoader';

import { ListingsContext } from '../../context/ListingsContext';
import { ListingsProvider } from '../../context/ListingsProvider';
import { useStoreAnonymousPSA } from '../../hooks/useStoreAnonymousPSA';
import { SearchView } from '../../types';
import { adjustListingRequestForApiCall } from '../../utilities/adjustListingRequestForApiCall';
import { populateListingRequestFromUrl } from '../../utilities/populateListingRequestFromUrl';

import { Search } from './components/Search';
import { SearchViews } from './components/SearchViews';
import { InvalidLocationError } from './errors';
import { useMetaTags } from './hooks/useMetaTags';

type ListingSearchCityRouteParams = Parameters<
  typeof ROUTES.ListingSearchCity.url
>[0];

interface ListingsProps {
  listingRequest: ListingRequest;
  listings: FindListingsResponse;
}

export const getServerSideProps = definePageLoader<ListingsProps>(async ctx => {
  try {
    const listingRequestFromUrl = populateListingRequestFromUrl(
      getServerDataCache(),
      ctx.query as ListingSearchCityRouteParams,
    );

    if (!listingRequestFromUrl) {
      return {
        notFound: true,
      };
    }

    let previousSearchCriteria = {} as ListingSearchCriteriaResponse;

    try {
      await ctx.req.apiClient.listing
        .getListingSearchCriteria({
          psaIdentifier: (ctx.query as ListingSearchCityRouteParams)
            .psaIdentifier,
        })
        .then(searchCriteriaResponse => {
          previousSearchCriteria = searchCriteriaResponse;
        });
    } catch (ex) {
      logger.error(
        `Listings page, failed to fetch ListingSearchCriteria, ${ex}`,
      );
      previousSearchCriteria = {} as ListingSearchCriteriaResponse;
    }

    const isAuthenticated = !!ctx.req.authState.userProfile;

    const showSignupCard = !isAuthenticated;

    const rowsPerPage = showSignupCard
      ? LISTING_ROWS_PER_PAGE - 1
      : LISTING_ROWS_PER_PAGE;

    const listingRequest: ListingRequest = {
      ...previousSearchCriteria,
      ...listingRequestFromUrl,
      rowsPerPage,
    };

    const listings = await ctx.req.apiClient.listing.findListings({
      listingRequest: adjustListingRequestForApiCall(listingRequest),
    });

    return {
      props: {
        listingRequest,
        listings,
        analytics: {
          name: getListingAnalyticsPageName(listingRequest.listingTypeIds),
          category: AnalyticsPageCategory.SearchResults,
        },
      },
    };
  } catch (error) {
    if (error instanceof InvalidLocationError) {
      logger.info(error.toJSON());

      return {
        notFound: true,
      };
    }

    throw error;
  }
});

const ListingsInternal = () => {
  const router = useRouter();
  const { listingsState } = ListingsContext.useContext();
  const { filters } = listingsState.filterResult;

  const { metaTitle, metaDescription } = useMetaTags();

  const { streetName, searchview } = filters;

  const selectedRoute = streetName
    ? ROUTES.ListingSearchStreet
    : ROUTES.ListingSearchCity;

  const streetSearchRouteParams = {
    typeAndCity: router.query.typeAndCity as string,
    street: router.query.street as string,
  };
  const citySearchRouteParams = {
    typeAndCity: router.query.typeAndCity as string,
  };

  const selectedRouteParams = streetName
    ? streetSearchRouteParams
    : citySearchRouteParams;

  return (
    <PageLayout
      pageTitle={metaTitle}
      metaDescription={metaDescription}
      route={selectedRoute as typeof ROUTES.ListingSearchCity}
      routeParams={{
        ...selectedRouteParams,
        typeAndCity: TYPEANDCITY_NL_EN_MAPPING(selectedRouteParams.typeAndCity),
      }}
      nlRouteParams={{
        ...selectedRouteParams,
        typeAndCity: TYPEANDCITY_EN_NL_MAPPING(selectedRouteParams.typeAndCity),
      }}
      hideFooter={searchview === SearchView.Map}
    >
      <PageLayout.MainRaw>
        <Search />
        <SearchViews />
      </PageLayout.MainRaw>
    </PageLayout>
  );
};

const Listings: NextPage<ListingsProps> = ({ listingRequest, listings }) => {
  useStoreAnonymousPSA(listingRequest);

  return (
    <ListingsProvider
      initialListings={listings}
      initialFilters={{
        ...listingRequest,
      }}
    >
      <ListingsInternal />
    </ListingsProvider>
  );
};

export default Listings;
